/**
 * GreetingBar - who you are, what day it is, and where to start.
 *
 * "Start something" used to be its own card further down the page. It is the
 * natural second sentence of a welcome, so it lives here now: ONE container,
 * the greeting on top and the tool tiles under it as sunken wells (never a
 * second sheet of glass inside this one).
 *
 * It used to carry a focus line too ("3 tasks are overdue"), which made it the
 * tallest thing on the page and buried the one ACTIONABLE sentence on the
 * dashboard inside a decorative hero band. The focus moved to `NeedsAttention`,
 * which is a card of clickable rows sitting beside "At a glance" - where an
 * action belongs - and this went back to being what a greeting is: two lines,
 * quiet, out of the way.
 *
 * The champagne hairline down the left edge is the only accent: this is the
 * page's opening statement, and the accent marks it as such. Champagne is for
 * emphasis at that scale, never for filling a surface.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, RootState } from '../../../store';
import { getIntlLocale } from '../../../utils/functions';
import { ToolTiles, accessibleTools } from './ToolsGrid';
import { useLicenseGate } from '../../../hooks/useLicenseGate';

export interface GreetingBarProps {
  /** Unused - kept so an older caller does not break. See the note above. */
  tasks?: unknown[];
  events?: unknown[];
}

const GreetingBar: React.FC<GreetingBarProps> = () => {
  const { t, i18n } = useTranslation();
  const { hasSectionAccess } = useLicenseGate();
  const hasTools = accessibleTools(hasSectionAccess).length > 0;

  const userInfo = useAppSelector((state: RootState) => state.user.userInfo);
  const firstName = userInfo?.first_name || userInfo?.name?.split(' ')[0] || 'there';

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return t('Good morning');
    if (hour < 17) return t('Good afternoon');
    return t('Good evening');
  }, [t]);

  const dateStr = useMemo(() => {
    return new Intl.DateTimeFormat(getIntlLocale(), {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    }).format(new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  return (
    <div className="glass relative flex h-full min-h-0 flex-col gap-4 overflow-hidden rounded-lg px-6 py-5 shadow-sm">
      <div className="flex-shrink-0">
        <p className="text-[11px] font-medium text-fg-muted">{dateStr}</p>
        {/* Wrap rather than truncate: "Good afternoon, David" is longer than
            the morning greeting. line-clamp-2 lets it flow onto a second line
            and only ellipsizes a genuinely long name. */}
        <h1 className="mt-1.5 text-2xl font-medium leading-tight tracking-tight text-fg-strong line-clamp-2 [overflow-wrap:anywhere]">
          {greeting}, {firstName}
        </h1>
      </div>

      {hasTools && (
        <section
          className="custom-scrollbar-main flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto"
          aria-label={t('Start something')}
          data-tour-id="dashboard-tools"
          data-testid="greeting-quick-actions"
        >
          <p className="text-[11px] font-medium text-fg-muted">{t('Start something')}</p>
          <ToolTiles className="widget-metrics widget-metrics-3 gap-2" />
        </section>
      )}
    </div>
  );
};

export default GreetingBar;
